import { Controller } from "@hotwired/stimulus"

// Mobile-only slide-up video popover (docs/features/v1/video-player.md, Phase 4).
//
// Desktop is unchanged: the search-grid card link is a normal full-page nav to
// the watch page (copying YouTube web). Below the `md` breakpoint a tap opens a
// bottom sheet instead. On tap we start the slide-up animation AND fetch the
// watch page concurrently, so the YouTube iframe loads under the motion and the
// open feels instant. The authenticated URL is synced via pushState so back /
// forward and direct links behave; the signed /watch URL remains the public,
// anonymous fallback.
//
// Content comes from the authenticated player fragment, which renders the same
// shared player partial as the public watch page; we extract its #video_player.
export default class extends Controller {
  static targets = ["sheet", "panel", "content", "title", "channel", "handle", "bookmark", "template", "revealCard"]
  static values = {
    open: { type: Boolean, default: false },
    initialPlayerUrl: String,
    initialFallbackUrl: String,
    initialReturnUrl: String,
    initialTitle: String,
    initialChannel: String,
    initialThumbnailUrl: String
  }

  connect() {
    this.boundClick = this.handleClick.bind(this)
    this.boundPopState = this.handlePopState.bind(this)
    this.boundBeforeCache = this.forceClose.bind(this)
    this.boundTouchStart = this.handleTouchStart.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)
    this.boundSuppressClick = this.suppressClick.bind(this)
    this.boundPreviewStateChanged = this.handlePreviewStateChanged.bind(this)
    this.collapsed = false
    this.collapseMetrics = null
    this.element.addEventListener("click", this.boundClick)
    // Sheet gestures are deliberately delegated from the panel but can only
    // begin on the title bar or the tabs. Supporting content keeps native
    // scrolling and elastic overscroll without ever moving the sheet.
    this.panelTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    window.addEventListener("popstate", this.boundPopState)
    // Turbo caches a snapshot of the page before navigating away (and restores
    // it on back/forward). If we let it snapshot while the sheet is open, the
    // restored DOM brings back a visible, click-eating overlay that the freshly
    // connected controller (openValue defaults false) never hides. Force the
    // sheet shut synchronously before any snapshot is taken.
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    this.element.addEventListener("segment-player:previewStateChanged", this.boundPreviewStateChanged)

    if (this.hasInitialPlayerUrlValue) {
      this.initialOpenFrame = requestAnimationFrame(() => this.openInitialPlayer())
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundClick)
    window.removeEventListener("popstate", this.boundPopState)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.element.removeEventListener("segment-player:previewStateChanged", this.boundPreviewStateChanged)
    this.panelTarget.removeEventListener("touchstart", this.boundTouchStart)
    this.endDragListeners()
    document.removeEventListener("click", this.boundSuppressClick, true)
    if (this.initialOpenFrame) cancelAnimationFrame(this.initialOpenFrame)
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    if (this.collapseTimeout) clearTimeout(this.collapseTimeout)
    document.body.style.overflow = ""
  }

  // Delegated: only intercept a primary click on a watch link, on mobile, for an
  // embeddable video. Anything else falls through to normal navigation.
  handleClick(event) {
    if (event.defaultPrevented) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest('a[href*="/watch/"]')
    if (!link) return
    if (!this.isMobile()) return // desktop: let the link navigate

    const card = link.closest("[data-embeddable]")
    if (!card || card.dataset.embeddable === "false") return // not a card, or not embeddable → link out

    event.preventDefault()
    // The card's thumbnail is already loaded/cached; reuse it as an instant
    // poster so the sheet isn't black while the iframe loads.
    const thumb = card && card.querySelector(".search-result__thumbnail-image")
    this.openPopover(card.dataset.playerUrl || link.href, thumb ? thumb.currentSrc || thumb.src : null, {
      title: card ? card.dataset.title : null,
      channel: card ? card.dataset.channel : null,
      bookmarkButton: card.querySelector(".search-result__bookmark-button"),
      historyUrl: card.dataset.playerHistoryUrl,
      fallbackUrl: card.dataset.playerFallbackUrl || link.href
    })
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  openInitialPlayer() {
    if (!this.isMobile()) {
      window.location.replace(this.initialFallbackUrlValue || this.initialPlayerUrlValue)
      return
    }

    this.openPopover(this.initialPlayerUrlValue, this.initialThumbnailUrlValue || null, {
      title: this.initialTitleValue,
      channel: this.initialChannelValue,
      fallbackUrl: this.initialFallbackUrlValue,
      returnUrl: this.initialReturnUrlValue
    }, { pushHistory: false })
  }

  // The onboarding reveal already has the exact reviewed player markup in an
  // inert template. Open it through this same sheet controller so navigation,
  // gestures, collapse behaviour and teardown cannot drift from the app's
  // normal player again.
  openTemplate(event) {
    if (event) event.preventDefault()

    const countInGate = document.querySelector('[data-controller~="count-in-modal"]')
    if (countInGate && !countInGate.classList.contains("hidden")) return

    this.openTemplatePlayer({
      templateIndex: Number(event.params.index),
      title: event.params.title || "",
      channel: event.params.channel || "",
      historyUrl: window.location.href,
      returnUrl: window.location.href
    })
  }

  openTemplatePlayer(meta, options = {}) {
    const template = this.templateTargets[meta.templateIndex]
    if (!template) return

    this.currentPopover = { ...meta }
    this.contentTarget.replaceChildren(template.content.cloneNode(true))
    this.setHeader(meta.title, meta.channel)
    this.showSheet()
    if (options.pushHistory !== false) this.pushHistoryEntry(this.currentPopover)
  }

  handlePreviewStateChanged(event) {
    const index = this.currentPopover?.templateIndex
    if (index == null) return

    const state = event.detail?.state
    if (!["playing", "paused", "watched"].includes(state)) return

    this.updateRevealCard(index, state)
  }

  updateRevealCard(index, state) {
    const card = this.revealCardTargets.find((candidate) => Number(candidate.dataset.videoPopoverCardIndex) === Number(index))
    if (!card) return

    const status = card.querySelector("[data-reveal-card-status]")
    const cta = card.querySelector("[data-reveal-card-cta]")
    const labels = {
      playing: ["Watching now", "Playing"],
      paused: ["Paused", "Continue lesson"],
      watched: ["Watched", "Watch again"]
    }

    card.dataset.playbackState = state
    status.textContent = labels[state][0]
    cta.textContent = labels[state][1]
  }

  settleRevealCardState() {
    const index = this.currentPopover?.templateIndex
    if (index == null) return

    const card = this.revealCardTargets.find((candidate) => Number(candidate.dataset.videoPopoverCardIndex) === Number(index))
    if (!card || card.dataset.playbackState === "watched") return

    delete card.dataset.playbackState
    card.querySelector("[data-reveal-card-status]").textContent = card.dataset.videoPopoverOriginalStatus
    card.querySelector("[data-reveal-card-cta]").textContent = card.dataset.videoPopoverOriginalCta
  }

  // Start the slide and the fetch at the same moment (the motion masks the load).
  openPopover(url, thumbnailSrc = null, meta = {}, options = {}) {
    const pushHistory = options.pushHistory !== false
    this.thumbnailSrc = thumbnailSrc
    this.currentPopover = {
      playerUrl: url,
      historyUrl: meta.historyUrl || window.location.href,
      fallbackUrl: meta.fallbackUrl || url,
      returnUrl: meta.returnUrl || window.location.href,
      thumbnailSrc,
      title: meta.title || "",
      channel: meta.channel || ""
    }
    // Set the top-bar title/channel before the slide so it's correct as it rises.
    this.setHeader(meta.title, meta.channel, meta.bookmarkButton)
    this.showSheet()
    this.showThumbnailPlaceholder()
    if (pushHistory) this.pushHistoryEntry(this.currentPopover)
    this.loadPlayer(url)
  }

  // Populate the top-bar title + channel; hide the channel line when absent so it
  // doesn't leave an empty gap.
  setHeader(title, channel, bookmarkButton = null) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title || ""
    if (this.hasChannelTarget) {
      this.channelTarget.textContent = channel || ""
      this.channelTarget.classList.toggle("hidden", !channel)
    }
    this.setBookmarkButton(bookmarkButton)
  }

  // The header is outside the fetched #video_player fragment, so mirror the
  // existing shared bookmark button into it. Its bookmark identity classes let
  // the normal picker Turbo Stream refresh both this action and every card.
  setBookmarkButton(sourceButton) {
    if (!this.hasBookmarkTarget) return

    this.bookmarkTarget.replaceChildren()
    this.bookmarkTarget.className = "player-sheet__bookmark hidden"
    if (!sourceButton) return

    const sourceWrapper = sourceButton.closest('[class*="bookmark_video_"]')
    const markers = Array.from(sourceWrapper?.classList || []).filter((name) =>
      name.startsWith("bookmark_video_") || name.startsWith("bookmark_lesson_")
    )
    markers.forEach((marker) => this.bookmarkTarget.classList.add(marker))
    this.bookmarkTarget.appendChild(sourceButton.cloneNode(true))
    this.bookmarkTarget.classList.remove("hidden")
  }

  // Show the cached thumbnail immediately (before the fetch returns) so the
  // sliding sheet reveals the poster, not a black box.
  showThumbnailPlaceholder() {
    if (!this.thumbnailSrc) {
      this.contentTarget.replaceChildren()
      return
    }
    const wrap = document.createElement("div")
    wrap.className = "relative w-full aspect-[16/9] bg-black"
    wrap.appendChild(this.buildThumbnail())
    this.contentTarget.replaceChildren(wrap)
  }

  buildThumbnail() {
    const img = document.createElement("img")
    img.src = this.thumbnailSrc
    img.className = "absolute inset-0 w-full h-full object-cover"
    img.setAttribute("aria-hidden", "true")
    return img
  }

  showSheet() {
    if (this.hideTimeout) {
      // Re-opening before a previous close finished animating: cancel the pending
      // hide so it can't strip the player out from under us.
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.clearPanelDrag()
    this.resetCollapseLayout()
    document.body.style.overflow = "hidden"
    // Reveal at the start state: panel fully below the screen. The sheet only
    // captures taps once it's actually open — `pointer-events-none` is the closed
    // state, removed here and re-added the instant we close.
    this.sheetTarget.classList.remove("hidden", "pointer-events-none")
    this.sheetTarget.classList.add("flex")
    // Force a synchronous reflow so the browser PAINTS that start state before we
    // change it — otherwise the class removal below collapses into the same frame
    // and the panel appears already part-way up instead of rising from the bottom.
    void this.panelTarget.offsetHeight
    // Slide the (opaque) panel up to cover the screen. No backdrop fade — the
    // slide itself covers the page.
    this.panelTarget.classList.remove("translate-y-full")
    this.openValue = true
  }

  hideSheet() {
    // "Closed" is deterministic and immediate: the transparent z-50 sheet stops
    // capturing taps right now (pointer-events-none), even though it stays in the
    // layout for the slide-down. We do NOT rely on the timer below to make the
    // page interactive again — only to tidy up the (now inert) layer afterwards.
    // A swipe leaves an inline translate; animate from there to off-screen
    // instead of letting the class jump the panel back to the top first.
    this.settleRevealCardState()
    if (this.panelTarget.style.transform) {
      this.panelTarget.style.transition = "transform 500ms ease-in-out"
      this.panelTarget.style.transform = "translateY(100%)"
    }
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("pointer-events-none")
    document.body.style.overflow = ""
    this.openValue = false
    // After the slide-down finishes, fully hide the layer and clear the player so
    // the iframe stops and audio dies. Must match the panel's transition duration.
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null
      this.clearPanelDrag()
      this.sheetTarget.classList.add("hidden")
      this.sheetTarget.classList.remove("flex")
      this.contentTarget.replaceChildren()
      this.resetCollapseLayout()
      this.currentPopover = null
    }, 500)
  }

  // Synchronous, animation-free teardown. Used before Turbo snapshots the page so
  // a cached/restored DOM can never hold an open, click-swallowing overlay.
  forceClose() {
    this.settleRevealCardState()
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.endDragListeners()
    this.clearPanelDrag()
    this.resetCollapseLayout()
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("hidden", "pointer-events-none")
    this.sheetTarget.classList.remove("flex")
    this.contentTarget.replaceChildren()
    document.body.style.overflow = ""
    this.openValue = false
    this.pushed = false
    this.currentPopover = null
  }

  async loadPlayer(url) {
    try {
      const response = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      if (!response.ok) return this.fallbackToNav(url)

      const doc = new DOMParser().parseFromString(await response.text(), "text/html")
      const player = doc.querySelector("#video_player")
      if (!player) return this.fallbackToNav(url)

      const bookmarkButton = doc.querySelector(".video-details .search-result__bookmark-button")

      if (!this.openValue) return // closed during the fetch

      // Compact Recently Saved links do not carry a bookmark button, so fill it
      // from the fetched watch page. When a card already supplied one, leave
      // that live node untouched: replacing an identical button during an early
      // tap can detach the tap target and silently discard the interaction.
      if (bookmarkButton && this.hasBookmarkTarget && this.bookmarkTarget.childElementCount === 0) {
        this.setBookmarkButton(bookmarkButton)
      }

      // Importing + inserting connects the segment-player controller → iframe
      // loads. The fetched fragment carries its own poster (an img over the
      // stage); the segment-player keeps it until playback actually starts
      // (PLAYING) and removes it then, so the iframe loading state is never seen.
      // It matches the instant placeholder above (same thumbnail), so the swap is
      // seamless.
      this.contentTarget.replaceChildren(document.importNode(player, true))
      this.resetCollapseLayout()
    } catch (error) {
      this.fallbackToNav(url)
    }
  }

  fallbackToNav(url) {
    window.location.href = this.currentPopover?.fallbackUrl || url
  }

  // Preserve Turbo's existing history fields while adding the in-app player
  // state. The earlier implementation replaced history.state wholesale, which
  // let Turbo and the popover overwrite one another and forced a state-only URL.
  pushHistoryEntry(popover) {
    history.pushState({ ...(history.state || {}), videoPopover: popover }, "", popover.historyUrl)
    this.pushed = true
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.openValue) return

    if (this.pushed && history.state?.videoPopover) {
      // Pop our state entry; popstate closes the sheet and reveals the exact
      // originating URL + scroll position.
      this.pushed = false
      history.back()
    } else {
      this.restoreColdEntryUrl()
      this.hideSheet()
    }
  }

  restoreColdEntryUrl() {
    const returnUrl = this.currentPopover?.returnUrl
    if (!returnUrl || returnUrl === window.location.href) return

    const state = { ...(history.state || {}) }
    delete state.videoPopover
    history.replaceState(state, "", returnUrl)
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  // --- Sheet gestures ------------------------------------------------------
  // Expanded: pull either bar down to dismiss, or pull the tabs up to collapse
  // the video chrome. Collapsed: pull the tabs down to restore it. Supporting
  // content is never a gesture surface.

  handleTouchStart(event) {
    if (event.touches.length !== 1) return
    const surface = this.gestureSurfaceFrom(event.target)
    if (!surface) return

    const point = this.touchPoint(event)
    this.drag = {
      surface,
      operation: null,
      startY: point.y,
      startX: point.x,
      lastY: point.y,
      lastTime: point.time,
      velocity: 0,
      active: false
    }
    this.panelTarget.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    this.panelTarget.addEventListener("touchend", this.boundTouchEnd)
    this.panelTarget.addEventListener("touchcancel", this.boundTouchEnd)
  }

  handleTouchMove(event) {
    if (!this.drag) return
    const point = this.touchPoint(event)
    const dy = point.y - this.drag.startY
    const dx = point.x - this.drag.startX
    const dt = Math.max(point.time - this.drag.lastTime, 1)
    this.drag.velocity = (point.y - this.drag.lastY) / dt
    this.drag.lastY = point.y
    this.drag.lastTime = point.time

    if (!this.drag.active) {
      if (Math.abs(dy) < 12 && Math.abs(dx) < 12) return
      if (Math.abs(dx) > Math.abs(dy)) {
        this.endDragListeners()
        this.drag = null
        return
      }

      this.drag.operation = this.gestureOperation(this.drag.surface, dy)
      if (!this.drag.operation) {
        this.endDragListeners()
        this.drag = null
        return
      }

      if (["collapse", "expand"].includes(this.drag.operation) && !this.captureCollapseMetrics()) {
        this.endDragListeners()
        this.drag = null
        return
      }
      this.drag.active = true
    }

    event.preventDefault()
    if (this.drag.operation === "dismiss") {
      this.panelTarget.style.transition = "none"
      this.panelTarget.style.transform = `translateY(${Math.max(dy, 0)}px)`
    } else {
      const distance = Math.max(this.collapseDistance, 1)
      const progress = this.drag.operation === "collapse"
        ? Math.min(Math.max(-dy / distance, 0), 1)
        : 1 - Math.min(Math.max(dy / distance, 0), 1)
      this.applyCollapseProgress(progress)
    }
  }

  handleTouchEnd() {
    const drag = this.drag
    this.endDragListeners()
    this.drag = null
    if (!drag || !drag.active) return

    // The finger lift would otherwise click whatever is underneath (a chapter
    // row seeks; the poster starts playback).
    this.armClickSuppress()

    const dy = drag.lastY - drag.startY
    if (drag.operation === "dismiss") {
      const flung = drag.velocity > 0.5 && dy > 48
      const far = dy > Math.min(160, window.innerHeight * 0.22)
      if (flung || far) this.close()
      else this.snapSheetBack()
      return
    }

    const threshold = Math.min(120, window.innerHeight * 0.16)
    const collapse = drag.operation === "collapse"
    const distance = collapse ? -dy : dy
    const flung = collapse ? drag.velocity < -0.5 && distance > 48 : drag.velocity > 0.5 && distance > 48
    const committed = flung || distance > threshold
    this.setCollapsed(collapse ? committed : !committed)
  }

  gestureSurfaceFrom(target) {
    if (!this.openValue || !this.isMobile()) return null
    if (target.closest("[data-video-popover-gesture-ignore]")) return null
    if (this.hasHandleTarget && this.handleTarget.contains(target)) return "header"
    if (target.closest('[data-video-popover-gesture="tabs"]')) return "tabs"
    return null
  }

  gestureOperation(surface, dy) {
    if (surface === "header") return !this.collapsed && dy > 0 ? "dismiss" : null
    if (surface !== "tabs") return null
    if (this.collapsed) return dy > 0 ? "expand" : null
    return dy > 0 ? "dismiss" : "collapse"
  }

  touchPoint(event) {
    const touch = event.touches[0] || event.changedTouches[0]
    return { x: touch.clientX, y: touch.clientY, time: event.timeStamp }
  }

  snapSheetBack() {
    const panel = this.panelTarget
    panel.style.transition = "transform 200ms ease-out"
    panel.style.transform = "translateY(0px)"
    const done = () => {
      panel.removeEventListener("transitionend", done)
      if (this.openValue) this.clearPanelDrag()
    }
    panel.addEventListener("transitionend", done)
  }

  clearPanelDrag() {
    this.panelTarget.style.transition = ""
    this.panelTarget.style.transform = ""
  }

  captureCollapseMetrics() {
    if (this.collapseMetrics) return true

    const chrome = this.contentTarget.querySelector("[data-player-chrome]")
    if (!this.hasHandleTarget || !chrome) return false

    const headerStyle = window.getComputedStyle(this.handleTarget)
    const headerInset = Number.parseFloat(headerStyle.paddingTop) || 0
    this.collapseMetrics = [
      {
        element: this.handleTarget,
        expandedHeight: this.handleTarget.getBoundingClientRect().height,
        collapsedHeight: headerInset
      },
      {
        element: chrome,
        expandedHeight: chrome.getBoundingClientRect().height,
        collapsedHeight: 0
      }
    ]
    this.collapseDistance = this.collapseMetrics.reduce(
      (total, metric) => total + metric.expandedHeight - metric.collapsedHeight,
      0
    )
    return this.collapseDistance > 0
  }

  applyCollapseProgress(progress, animated = false) {
    if (!this.collapseMetrics) return

    const bounded = Math.min(Math.max(progress, 0), 1)
    this.collapseMetrics.forEach((metric) => {
      const height = metric.expandedHeight - ((metric.expandedHeight - metric.collapsedHeight) * bounded)
      metric.element.style.transition = animated ? "height 240ms ease-out, opacity 180ms ease-out" : "none"
      metric.element.style.height = `${Math.max(height, metric.collapsedHeight)}px`
      metric.element.style.minHeight = "0px"
      metric.element.style.overflow = "hidden"
      metric.element.style.opacity = `${1 - bounded}`
      metric.element.style.pointerEvents = bounded > 0.98 ? "none" : ""
    })
  }

  setCollapsed(collapsed) {
    if (!this.captureCollapseMetrics()) return

    if (collapsed && !this.collapsed) this.pauseCollapsedPlayer()
    this.collapsed = collapsed
    this.panelTarget.dataset.playerLayout = collapsed ? "collapsed" : "expanded"
    this.applyCollapseProgress(collapsed ? 1 : 0, true)

    if (this.collapseTimeout) clearTimeout(this.collapseTimeout)
    this.collapseTimeout = setTimeout(() => {
      this.collapseTimeout = null
      if (!this.collapsed) {
        this.resetCollapseStyles()
        this.collapseMetrics = null
        this.collapseDistance = null
      } else {
        this.collapseMetrics.forEach(({ element }) => { element.style.transition = "none" })
      }
    }, 260)
  }

  pauseCollapsedPlayer() {
    this.contentTarget.querySelector("#video_player")?.dispatchEvent(new CustomEvent("video-popover:collapse"))
  }

  resetCollapseLayout() {
    if (this.collapseTimeout) {
      clearTimeout(this.collapseTimeout)
      this.collapseTimeout = null
    }
    this.resetCollapseStyles()
    this.collapsed = false
    this.collapseMetrics = null
    this.collapseDistance = null
    if (this.hasPanelTarget) this.panelTarget.dataset.playerLayout = "expanded"
  }

  resetCollapseStyles() {
    const elements = new Set((this.collapseMetrics || []).map(({ element }) => element))
    if (this.hasHandleTarget) elements.add(this.handleTarget)
    const chrome = this.contentTarget.querySelector("[data-player-chrome]")
    if (chrome) elements.add(chrome)

    elements.forEach((element) => {
      element.style.removeProperty("transition")
      element.style.removeProperty("height")
      element.style.removeProperty("min-height")
      element.style.removeProperty("overflow")
      element.style.removeProperty("opacity")
      element.style.removeProperty("pointer-events")
    })
  }

  endDragListeners() {
    this.panelTarget.removeEventListener("touchmove", this.boundTouchMove)
    this.panelTarget.removeEventListener("touchend", this.boundTouchEnd)
    this.panelTarget.removeEventListener("touchcancel", this.boundTouchEnd)
  }

  armClickSuppress() {
    document.addEventListener("click", this.boundSuppressClick, true)
    // A drag that ends over empty space never produces a click. Drop the
    // one-shot listener so the next real tap is not swallowed.
    setTimeout(() => {
      document.removeEventListener("click", this.boundSuppressClick, true)
    }, 400)
  }

  suppressClick(event) {
    event.preventDefault()
    event.stopPropagation()
    document.removeEventListener("click", this.boundSuppressClick, true)
  }

  // Hardware back closes the sheet without unmounting the originating page;
  // forward restores the same player URL and reloads its fragment.
  handlePopState(event) {
    const popover = event.state?.videoPopover
    if (popover && this.isMobile()) {
      this.pushed = true
      if (Number.isInteger(popover.templateIndex)) {
        this.openTemplatePlayer(popover, { pushHistory: false })
      } else {
        this.openPopover(popover.playerUrl, popover.thumbnailSrc, popover, { pushHistory: false })
      }
      return
    }

    if (!this.openValue) return
    this.pushed = false
    this.hideSheet()
  }
}
